// Contact-reaction rules for Full 3D Mode, parsed from the engine's
// own reactions.yaml so the 3D world reacts by the same rules the 2D
// sim does (same source of truth — no separate reaction list to drift).
//
// We only lift the *contact* / *combine* reactions: a single reactant
// A that transforms when it touches material B
// (rate: { kind: combine|contact, with: B }). Those are the ones that
// read naturally as "place A next to B and they react" — water + lava
// → steam + basalt, sodium + water → fire + steam, acid + metal, fire
// spreading into wood, and so on. Temperature-gated decays (water →
// ice, lava → basalt over time) are left out: the 3D playground has no
// temperature field, so firing them on contact alone would be wrong.

import { parseYaml } from "../../sim/yaml";

export interface ContactRule {
  /** What the reactant A becomes when it touches `with`. */
  become: string;
  /** Optional second product, dropped into a free neighbour cell
   * (e.g. the `steam` from water + lava). `null` when the reaction
   * has a single product. */
  spawn: string | null;
}

interface RawReactant {
  id?: unknown;
}
interface RawProduct {
  id?: unknown;
}
interface RawRate {
  kind?: unknown;
  with?: unknown;
}
interface RawReaction {
  reactants?: unknown;
  products?: unknown;
  rate?: unknown;
}

/** Parse reactions.yaml text into a `"A|B" → rule` map: reactant A
 * touching material B turns A into `become` (and optionally spawns a
 * second product). */
export function parseContactReactions(yaml: string): Map<string, ContactRule> {
  const out = new Map<string, ContactRule>();
  const root = parseYaml(yaml) as { reactions?: RawReaction[] };
  if (!root || !Array.isArray(root.reactions)) return out;
  for (const r of root.reactions) {
    if (!r || typeof r !== "object") continue;
    const rate = r.rate as RawRate | undefined;
    if (!rate || (rate.kind !== "combine" && rate.kind !== "contact")) continue;
    if (typeof rate.with !== "string") continue;
    const reactants = r.reactants as RawReactant[] | undefined;
    const products = r.products as RawProduct[] | undefined;
    if (!Array.isArray(reactants) || reactants.length !== 1) continue;
    if (!Array.isArray(products) || products.length === 0) continue;
    const a = reactants[0]?.id;
    const become = products[0]?.id;
    if (typeof a !== "string" || typeof become !== "string") continue;
    const spawn = typeof products[1]?.id === "string" ? (products[1].id as string) : null;
    // First rule wins for a given pair (the file lists the primary
    // reaction before niche variants).
    const key = `${a}|${rate.with}`;
    if (!out.has(key)) out.set(key, { become, spawn });
  }
  return out;
}
